/**
 * Carbon-Cost Joint Optimizer (Patent Sub-system P)
 * Pareto-frontier multi-objective optimisation of $ AND kg-CO2.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Slider } from '../components/ui/slider';
import { Loader2, FlaskConical, Sparkles, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import DemoDataBanner from '../components/DemoDataBanner';
import BrandTitle from '../components/BrandTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtUsd = (n) => `$${(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const fmtKg = (n) => `${(n || 0).toLocaleString('en-US', { maximumFractionDigits: 1 })} kg`;

const CarbonOptimizer = () => {
  const [weight, setWeight] = useState(0.5);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = async (w) => {
    setLoading(true);
    try {
      const r = await axios.post(`${API_URL}/carbon-cost/recommend`, {
        workload_count: 10, carbon_weight: w,
        current_id: 'm5.xlarge_us-east-2_ondemand',
      });
      setData(r.data);
    } catch { toast.error('Could not load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { reload(0.5); }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="carbon-optimizer-page">
      <Helmet><title>Carbon-Cost Joint Optimizer | CARTIEAI</title></Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <DemoDataBanner />
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-green-300 border border-green-500/30 rounded-md px-2 py-1 mb-5" data-testid="co-patent-badge">
            <FlaskConical className="w-3 h-3" /> Proprietary engine
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05]"><BrandTitle>Carbon-Cost Joint Optimizer</BrandTitle></h1>
          <p className="mt-4 text-base sm:text-lg text-slate-300 leading-relaxed">
            Cloud Carbon Footprint, AWS, and Google all <span className="text-white font-semibold">report</span> CO2. <span className="text-green-300">Nobody optimises jointly with $.</span>
          </p>
          <p className="mt-3 text-sm text-slate-500">Pareto-frontier optimisation across (instance × region × commitment). Tenant-configurable cost-vs-carbon tradeoff.</p>
        </div>

        <Card className="mt-10 bg-slate-900/60 border border-white/10 shadow-sm border-white/10">
          <CardHeader>
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-green-300" /> Tradeoff weight: <span className="font-mono">{(weight * 100).toFixed(0)}% carbon</span>
            </CardTitle>
            <CardDescription>Move the slider and click recompute — Pareto recommendation updates.</CardDescription>
          </CardHeader>
          <CardContent>
            <Slider value={[weight * 100]} onValueChange={(v) => setWeight(v[0] / 100)} max={100} step={5} className="my-4" data-testid="co-weight-slider" />
            <div className="flex justify-between text-xs text-slate-500 font-mono">
              <span>100% cost</span><span>balanced</span><span>100% carbon</span>
            </div>
            <button onClick={() => reload(weight)} className="mt-4 bg-green-500 hover:bg-green-400 text-slate-950 font-semibold px-4 py-2 rounded text-sm" data-testid="co-recompute-btn">
              Recompute Pareto frontier
            </button>
          </CardContent>
        </Card>

        {loading && <Loader2 className="w-8 h-8 animate-spin mx-auto my-12 text-slate-500" />}

        {data && (
          <div className="mt-8 grid md:grid-cols-2 gap-4">
            <Card className="bg-slate-900/60 border border-white/10 shadow-sm border-rose-500/30">
              <CardContent className="p-5">
                <div className="text-[10px] uppercase tracking-widest text-rose-300 mb-2">Your current placement</div>
                <div className="text-white font-mono text-lg" data-testid="co-current">{data.current_placement?.instance} @ {data.current_placement?.region}</div>
                <div className="text-xs text-slate-500 font-mono mt-1">{data.current_placement?.commitment}</div>
                <div className="flex gap-4 mt-3 text-sm">
                  <div className="text-white font-mono font-semibold">{fmtUsd(data.current_placement?.monthly_cost_usd)}/mo</div>
                  <div className="text-rose-300 font-mono font-semibold">{fmtKg(data.current_placement?.monthly_kg_co2)}/mo</div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-500/15 to-emerald-500/5 border-green-500/40">
              <CardContent className="p-5">
                <div className="text-[10px] uppercase tracking-widest text-green-300 mb-2">Pareto-optimal recommendation</div>
                <div className="text-white font-mono text-lg" data-testid="co-recommendation">{data.recommendation?.instance} @ {data.recommendation?.region}</div>
                <div className="text-xs text-slate-500 font-mono mt-1">{data.recommendation?.commitment}</div>
                <div className="flex gap-4 mt-3 text-sm">
                  <div className="text-white font-mono font-semibold">{fmtUsd(data.recommendation?.monthly_cost_usd)}/mo</div>
                  <div className="text-green-300 font-mono font-semibold">{fmtKg(data.recommendation?.monthly_kg_co2)}/mo</div>
                </div>
                {data.delta_vs_current && (
                  <div className="mt-4 text-sm" data-testid="co-delta">
                    <ArrowDown className="w-3 h-3 inline text-green-400" /> <span className="text-green-300 font-mono font-semibold">{data.delta_vs_current.cost_pct}%</span> cost
                    &nbsp;·&nbsp;<ArrowDown className="w-3 h-3 inline text-green-400" /> <span className="text-green-300 font-mono font-semibold">{data.delta_vs_current.co2_pct}%</span> CO2
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {data?.alternatives?.length > 0 && (
          <Card className="mt-8 bg-slate-900/60 border border-white/10 shadow-sm border-white/10">
            <CardHeader><CardTitle className="text-white text-lg">Next-best Pareto alternatives</CardTitle></CardHeader>
            <CardContent>
              <table className="w-full text-sm" data-testid="co-alternatives">
                <thead className="text-[10px] uppercase tracking-widest text-slate-500">
                  <tr className="border-b border-white/10">
                    <th className="text-left p-2">Instance</th><th className="text-left p-2">Region</th><th className="text-left p-2">Commit</th>
                    <th className="text-right p-2">$/mo</th><th className="text-right p-2">CO2/mo</th><th className="text-right p-2">Score</th>
                  </tr>
                </thead>
                <tbody>
                  {data.alternatives.map((a, i) => (
                    <tr key={i} className="border-b border-white/5 hover:bg-slate-950/60">
                      <td className="p-2 text-xs text-white font-mono">{a.instance}</td>
                      <td className="p-2 text-xs text-slate-200 font-mono">{a.region}</td>
                      <td className="p-2 text-xs text-slate-300">{a.commitment}</td>
                      <td className="p-2 text-xs text-right text-white font-mono">{fmtUsd(a.monthly_cost_usd)}</td>
                      <td className="p-2 text-xs text-right text-green-300 font-mono">{fmtKg(a.monthly_kg_co2)}</td>
                      <td className="p-2 text-xs text-right text-slate-500 font-mono">{a.weighted_score}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CarbonOptimizer;
